/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * The component for Money Transfer final step
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.2.0
 */

import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '@assets/index';
import type { IAccountSelectItem } from '@components/AccountSelect/AccountSelectTypes';
import { ImgStyled } from '@components/common/Block/Img';
import { LayoutStyled } from '@components/common/Block/Layout';
import { parseMoneyToFloat } from '@components/common/MoneyInput/utils/parseMoneyToFloat';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import type ICommonTranslation from '@modules/internationalization/types/common';
import type { IMoneyTransferTranslation } from '@modules/internationalization/types/wizards/moneyTransfer';
import WizardReport from '@modules/wizard/components/WizardReport/WizardReport';
import { EWizardReportParam } from '@modules/wizard/components/WizardReport/WizardReportConstants';
import type { IWizardSummaryParam } from '@modules/wizard/components/WizardSummary/WizardSummary';
import { WizardSummary } from '@modules/wizard/components/WizardSummary/WizardSummary';
import type {
  IWizardReportSetParamPayload,
  IWizardValidationError,
} from '@modules/wizard/wizardTypes';
import type { IRootReducer } from '@reducers/rootReducer';
import { formatBalance } from '@utils/formatText';

import {
  moneyTransferStorageSaveStep,
  moneyTransferValidateStart,
} from '../moneyTransferActions';
import {
  makeSelectMoneyTransferAmount,
  makeSelectMoneyTransferGetConflicts,
  makeSelectMoneyTransferSelectedAccount,
} from '../moneyTransferSelectors';
import type { IMoneyTransferStorageSaveStepPayload } from '../types/moneyTransferActionsTypes';
import { EMoneyTransferParamType } from '../types/moneyTransferTypes';
import {
  MoneyTransferConflictItemStyled,
  MoneyTransferFinalStepContainerStyled,
} from './styled/MoneyTransferStyled';

export interface IMoneyTransferStepFinal {
  moneyTransferTranslation: IMoneyTransferTranslation;
}

export interface IConnectedProps {
  conflicts: IWizardValidationError[];
  selectedAccountFrom: IAccountSelectItem;
  selectedAccountTo: IAccountSelectItem;
  transferAmount: string;
  translationCommon: ICommonTranslation;
}

export interface IDispatchProps {
  storageSaveStep: (payload: IMoneyTransferStorageSaveStepPayload) => void;
  validateStart: () => void;
}

export type TMoneyTransferStepFinal = IMoneyTransferStepFinal &
  IConnectedProps &
  IDispatchProps;

export const MoneyTransferStepFinal = (props: TMoneyTransferStepFinal) => {
  const moneyTransferDisplayParams: IWizardSummaryParam[] = [
    {
      title: props.moneyTransferTranslation.labelAccountFrom,
      values: [
        props.selectedAccountFrom.number,
        props.selectedAccountFrom.name,
        `${formatBalance(props.selectedAccountFrom.balance)} ${
          props.translationCommon.currency
        }`,
      ],
    },
    {
      title: props.moneyTransferTranslation.labelAccountTo,
      values: [
        props.selectedAccountTo.number,
        props.selectedAccountTo.name,
        `${formatBalance(props.selectedAccountTo.balance)} ${
          props.translationCommon.currency
        }`,
      ],
    },
    {
      title: props.moneyTransferTranslation.transferAmount.label,
      values: [
        `${formatBalance(parseMoneyToFloat(props.transferAmount))} ${
          props.translationCommon.currency
        }`,
      ],
    },
  ];

  const delayedHandleNotificationChange = debounce(
    (payload: IWizardReportSetParamPayload) => {
      const storageParams =
        payload.paramType === EWizardReportParam.EMAIL
          ? { notificationEmail: true }
          : { notificationPhone: true };
      props.storageSaveStep(storageParams);
    },
    DEBOUNCE_DEFAULT_DELAY,
  );

  return (
    <MoneyTransferFinalStepContainerStyled
      data-container={'money-transfer-final-step'}
    >
      <div>
        {!props.conflicts.length && (
          <WizardReport
            notificationValidate={props.validateStart}
            onChangeParam={delayedHandleNotificationChange}
          />
        )}
        {!!props.conflicts.length && (
          <LayoutStyled className={'mb-3'}>
            {props.moneyTransferTranslation.hintTextConflicts}
          </LayoutStyled>
        )}
        {props.conflicts.map((error) => (
          <MoneyTransferConflictItemStyled key={`${error.field}-${error.code}`}>
            <ImgStyled
              className="icon small"
              src={icons.attentionInvertIcon}
              width="16px"
            />
            {props.moneyTransferTranslation.error.codes[error.code]}
          </MoneyTransferConflictItemStyled>
        ))}
      </div>
      <div>
        <WizardSummary params={moneyTransferDisplayParams} />
      </div>
    </MoneyTransferFinalStepContainerStyled>
  );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>(
  {
    conflicts: makeSelectMoneyTransferGetConflicts(),
    selectedAccountFrom: makeSelectMoneyTransferSelectedAccount(
      EMoneyTransferParamType.transferAccountFrom,
    ),
    selectedAccountTo: makeSelectMoneyTransferSelectedAccount(
      EMoneyTransferParamType.transferAccountTo,
    ),
    transferAmount: makeSelectMoneyTransferAmount(),
    translationCommon: makeSelectTranslationCommon(),
  },
);

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  storageSaveStep: (payload) => dispatch(moneyTransferStorageSaveStep(payload)),
  validateStart: () => dispatch(moneyTransferValidateStart()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MoneyTransferStepFinal);
